import styled, { MQ, MQ_TABLET, Theme } from '../../constants/theme'
import { updateHexLuminance } from '../../lib/color'
import { FormContextMethods, FormErrors } from './FormCollector'
import { ValidationFunction } from './Validations'

// these are only really used for auth and registration forms
export enum BasicType {
  FOOTER = 'footer',
  PLAINTEXT = 'plaintext',
  SUBMIT = 'submit',
}

export enum ElementType {
  CHECKBOX = 'checkbox',
  DATE = 'date',
  DURATION = 'duration',
  EMAIL = 'email',
  PASSWORD = 'password',
  RADIO = 'radio',
  SELECT = 'select',
  SWITCH = 'switch',
  TEL = 'tel',
  TEXT = 'text',
  TEXTAREA = 'textarea',
  ZIP = 'zipcode',
}

export enum QuestionType {
  MULTISELECT = 'multiselect',
}

export enum ElementState {
  INDETERMINATE = 'INDETERMINATE',
  INVALID = 'INVALID',
  REQUEST = 'REQUEST',
  SUBMITTED = 'SUBMITTED',
  VALID = 'VALID',
}

export interface Field {
  description?: string
  display?: string
  falsyValues?: string[]
  hint?: { header: string; image?: string; text?: string }
  icon?: string
  justify?: 'center' | 'left' | 'right'
  label: string
  modal?: { body: string; header: string; trackEventName?: string }
  name?: string
  options?: SelectOption[]
  required?: boolean
  submitOverride?: { pattern: string; replace: string }
  type: BasicType | ElementType | QuestionType
  validPattern?: string
  value?: string
}

export interface FormElementProps extends FormContextMethods {
  disabled?: boolean
  display?: string
  description?: string
  errorText?: string
  icon?: JSX.Element
  id?: string
  includeBlank?: string
  isFocused?: boolean
  isRequired?: boolean
  label: string
  name?: string
  onChange?: (e: any) => void
  onEdit?: () => void
  onInput?: (e: any) => void
  onKeyUp?: (e: any) => void
  onPaste?: (e: any) => void
  options?: SelectOption[]
  placeholder?: string | null
  requiresPassword?: boolean
  rightButton?: JSX.Element
  rightIcon?: JSX.Element
  serverErrors?: FormErrors | null
  showCharsRemaining?: number
  elementState?: ElementState
  type: BasicType | ElementType | QuestionType
  unifiedErrorText?: string
  validations?: ValidationFunction[]
  value?: string | null
}

export interface CommonFormElementProps extends FormElementProps {
  key: string
}

export interface SelectOption {
  label: string
  value: number | string
}

export interface FormElementState {
  elementState: ElementState
  serverErrors?: FormErrors | null
  value?: string | null
}

export const bottomBorderForType = (
  type: BasicType | ElementType | QuestionType,
  theme: Theme,
): string => {
  switch (type) {
    case ElementType.DATE:
    case ElementType.DURATION:
    case ElementType.CHECKBOX:
    case ElementType.RADIO:
    case ElementType.SWITCH:
      return 'none'
    default:
      return `2px solid ${updateHexLuminance(theme.greyLight, -0.1)}`
  }
}

export const EditAvatar = styled.div({
  flex: '0 0 154px',
  height: 154,
  marginRight: 20,
  width: 154,
})

export const Column = styled.div({
  width: '100%',
})

export const Label = styled.label<{ icon?: JSX.Element }>(
  {
    fontSize: 16,
    left: 0,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    transition: 'all 0.2s ease',
  },
  ({ icon, theme }) => ({
    color: theme.text,
    fontFamily: theme.fontFamily,
    marginLeft: icon ? 30 : 0,
  }),
)

const sharedRightStyles: any = {
  bottom: 0,
  position: 'absolute',
  right: -5,
  transform: 'scale(0.7)',
}

export const RightButtonContainer = styled.div(
  sharedRightStyles,
  ({ theme }) => ({
    fill: theme.text,
  }),
)

export const RightIconContainer = styled.div(
  {
    ...sharedRightStyles,
    pointerEvents: 'none',
  },
  ({ theme }) => ({
    fill: theme.text,
  }),
)

export const Bar = styled.span(
  {
    '&::after': {
      right: '50%',
    },
    '&::before': {
      left: '50%',
    },
    '&::before, &::after': {
      bottom: -2,
      content: '""',
      height: 2,
      position: 'absolute',
      width: 0,
    },
    display: 'block',
    position: 'relative',
    width: '100%',
  },
  ({ theme }) => ({
    '&::before, &::after': {
      background: theme.primary,
      transition: `all ${theme.speed} ${theme.ease}`,
    },
  }),
)

export const FlexRow = styled.div({
  ...MQ_TABLET({
    '.SettingsMedical &, &.AccountDetails, &.Profile, &.Profile &': {
      display: 'block',
    },
    display: 'flex',
  }),
  [MQ.LARGE_DESKTOP]: {
    'html[style*="16px"] .SettingsMedical &, html[style*="16px"] &.AccountDetails, html[style*="16px"] &.Profile, html[style*="16px"] &.Profile &': {
      display: 'flex',
    },
  },
  [MQ.X_LARGE_DESKTOP]: {
    'html[style*="px"] .SettingsMedical &, html[style*="px"] &.AccountDetails, html[style*="px"] &.Profile, html[style*="px"] &.Profile &': {
      display: 'flex',
    },
  },
  '& > div:first-of-type': {
    marginBottom: 15,
    marginRight: 20,
  },
  display: 'block',
  marginBottom: 10,
  width: '100%',
})

export const PillDiv = styled.div({
  textAlign: 'right',
})
